import { GridBaseDataProvider } from '../../../grid/GridBaseDataProvider';
import {
  Column,
  ColumnKeys,
  SetFiltersRequest,
  GetDataGridItemsResponse,
  UpdateSettingsRequest,
  INVESTIGATION_ACTIVITIES,
} from '../types';
import { GetRowsRequest, GetSummaryRequest, GetSummaryResponse } from '../../../grid/types/common';
import { sanitizeObject } from '../../../utils/sanitization.utils';
import { getRequestDefaultOptions } from '../../../utils/request.utils';

export class ActivityGridDataProvider extends GridBaseDataProvider<Column, ColumnKeys> {
  private endpointId?: string;
  private limit = 50;

  constructor(public dependencies: any) {
    super();
  }

  async retrieveInvestigationFileActivity(itemId: string): Promise<void> {
    await this.dependencies.api.clients.CollectInvestigationPackageActivity.retrieveInvestigationFile(
      {
        endpointId: this.dependencies.endpointId,
        activityId: itemId,
      },
      getRequestDefaultOptions()
    );
  }

  async cancelActivityItem(itemId: string, itemType: number) {
    if (itemType === INVESTIGATION_ACTIVITIES.COLLECT_INVESTIGATION_PACKAGE) {
      await this.dependencies.api.clients.CollectInvestigationPackageActivity.cancelActivity(
        {
          activityId: itemId,
          endpointId: this.dependencies.endpointId,
        },
        getRequestDefaultOptions()
      );
    }

    if (itemType === INVESTIGATION_ACTIVITIES.REMOTE_SHELL_DOWNLOAD) {
      await this.dependencies.api.clients.RemoteShellActivity.cancelActivity(
        {
          activityId: itemId,
          endpointId: this.dependencies.endpointId,
        },
        getRequestDefaultOptions()
      );
    }
  }

  async downloadInvestigationFileActivity(itemId: string) {
    await this.dependencies.api.clients.EndpointInvestigationsActivities.downloadInvestigationFile(
      {
        endpointId: this.dependencies.endpointId,
        activityId: itemId,
      },
      getRequestDefaultOptions()
    );
  }

  async getActivityDetails(itemId: string) {
    return await this.dependencies.api.clients.EndpointInvestigationsActivities.getErrorDetails(
      {
        endpointId: this.dependencies.endpointId,
        activityId: itemId,
      },
      getRequestDefaultOptions()
    );
  }

  async deleteAllActivities() {
    await this.dependencies.api.clients.EndpointInvestigationsActivities.deleteAllRecords(
      {
        endpointId: this.dependencies.endpointId,
      },
      getRequestDefaultOptions()
    );
  }

  async getActivitiesNumberForEndpoint() {
    return await this.dependencies.api.clients.EndpointInvestigationsActivities.getNumberOfRecords(
      {
        endpointId: this.dependencies.endpointId,
      },
      getRequestDefaultOptions()
    );
  }

  async getSummary(request: GetSummaryRequest<ColumnKeys>): Promise<GetSummaryResponse> {
    const noOfItems = await this.getActivitiesNumberForEndpoint();

    return sanitizeObject({
      summary: this.dependencies.translateFn('{noOfItems} items', {
        noOfItems: noOfItems,
      }),
      numItems: noOfItems,
    });
  }

  async getRows(request: GetRowsRequest<Column, ColumnKeys>): Promise<GetDataGridItemsResponse> {
    request.pageSize = this.limit;
    request.extraParams['endpointId'] = this.dependencies.endpointId;

    const response = await this.dependencies.api.clients.EndpointInvestigationsActivities.readRecords(
      request,
      getRequestDefaultOptions()
    );

    let data = response.data;

    return sanitizeObject({ rawEvents: data });
  }

  async updateSettings(request: UpdateSettingsRequest): Promise<{}> {
    this.limit = request.pageSize ?? this.limit;
    return {};
  }

  async setFilters(request: SetFiltersRequest): Promise<{}> {
    return {};
  }
}
